import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { hospitalAdminApi } from "@/api/hospitalAdmin";
import { socialApi, type SocialChannelRow, type SocialWebhookMeta } from "@/api/social";
import { toast } from "sonner";
import { Copy } from "lucide-react";

const emptyForm = {
	id: "" as string | undefined,
	externalAccountId: "",
	pageId: "",
	accessToken: "",
	leadThreshold: 80,
	aiEnabled: false,
	autoReplyEnabled: false,
	status: "ACTIVE" as "ACTIVE" | "INACTIVE",
	defaultAssigneeTeamIds: [] as string[],
};

export default function SocialSettingsPage() {
	const qc = useQueryClient();
	const [form, setForm] = useState(emptyForm);

	const channelsQuery = useQuery({
		queryKey: ["social-channels"],
		queryFn: () => socialApi.listChannels(),
	});
	const webhookQuery = useQuery<SocialWebhookMeta>({
		queryKey: ["social-webhook-meta"],
		queryFn: () => socialApi.webhookMeta(),
	});
	const assigneesQuery = useQuery({
		queryKey: ["crm-rr-assignees"],
		queryFn: () => hospitalAdminApi.listRoundRobinAssignees(),
	});

	const saveMut = useMutation({
		mutationFn: () =>
			socialApi.upsertChannel({
				id: form.id || undefined,
				externalAccountId: form.externalAccountId.trim(),
				pageId: form.pageId.trim() || null,
				accessToken: form.accessToken.trim() || undefined,
				leadThreshold: form.leadThreshold,
				aiEnabled: form.aiEnabled,
				autoReplyEnabled: form.autoReplyEnabled,
				status: form.status,
				defaultAssigneeTeamIds: form.defaultAssigneeTeamIds,
			}),
		onSuccess: () => {
			toast.success("Channel saved");
			setForm(emptyForm);
			qc.invalidateQueries({ queryKey: ["social-channels"] });
		},
		onError: (e: any) => toast.error(e?.response?.data?.message || e.message || "Save failed"),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => socialApi.deleteChannel(id),
		onSuccess: () => {
			toast.success("Channel deleted");
			qc.invalidateQueries({ queryKey: ["social-channels"] });
		},
		onError: (e: any) => toast.error(e?.response?.data?.message || e.message || "Delete failed"),
	});

	const diagnoseMut = useMutation({
		mutationFn: (id: string) => socialApi.diagnoseChannel(id, { sendTestMessage: false }),
		onSuccess: (data: any) => {
			const hints = Array.isArray(data?.hints) ? data.hints : [];
			const scopes = data?.probe?.raw?.scopes;
			const scopeHint =
				Array.isArray(scopes) && scopes.length
					? `scopes: ${scopes.slice(0, 8).join(", ")}`
					: null;
			if (hints.length) {
				toast.error(hints.slice(0, 2).join(" | "), { duration: 12_000 });
			} else if (data?.probe?.ok) {
				toast.success(
					`Token OK — /me ${data.probe.meId || "?"} (${data.probe.meName || "ok"}) · autoAsk=${data.channel?.autoReplyEnabled ? "ON" : "OFF"}${scopeHint ? ` · ${scopeHint}` : ""}`,
					{ duration: 10_000 }
				);
			} else {
				toast.message("Diagnose complete — open browser console for full JSON");
			}
			console.info("[instagram diagnose]", data);
		},
		onError: (e: any) => toast.error(e?.response?.data?.message || e.message || "Diagnose failed"),
	});

	useEffect(() => {
		if (!form.id) return;
	}, [form.id]);

	function editChannel(ch: SocialChannelRow) {
		setForm({
			id: ch.id,
			externalAccountId: ch.externalAccountId,
			pageId: ch.pageId || "",
			accessToken: "",
			leadThreshold: ch.leadThreshold,
			aiEnabled: ch.aiEnabled,
			autoReplyEnabled: ch.autoReplyEnabled,
			status: ch.status,
			defaultAssigneeTeamIds: ch.defaultAssigneeTeamIds || [],
		});
	}

	function copy(text: string) {
		void navigator.clipboard.writeText(text);
		toast.success("Copied");
	}

	const channels: SocialChannelRow[] = channelsQuery.data ?? [];
	const assignees = assigneesQuery.data?.assignees ?? [];

	return (
		<div className="space-y-6 p-4 md:p-6">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold">Instagram channel settings</h1>
					<p className="text-sm text-muted-foreground">
						Paste Instagram Business Account ID + access token (Phase 1 — no OAuth).
					</p>
				</div>
				<Link className="text-sm underline-offset-4 hover:underline" to="/hospital-admin/social/inbox">
					Open inbox
				</Link>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Webhook</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm">
					<div className="flex flex-wrap items-center gap-2">
						<code className="rounded bg-slate-100 px-2 py-1 text-xs">
							{webhookQuery.data?.callbackUrl || "…"}
						</code>
						<Button
							type="button"
							size="sm"
							variant="outline"
							onClick={() => webhookQuery.data?.callbackUrl && copy(webhookQuery.data.callbackUrl)}
						>
							<Copy className="mr-1 h-3.5 w-3.5" />
							Copy URL
						</Button>
					</div>
					<p className="text-muted-foreground">
						Verify token env: <code>{webhookQuery.data?.verifyTokenEnv}</code> · App secret:{" "}
						<code>{webhookQuery.data?.appSecretEnv}</code>
					</p>
					<p className="text-muted-foreground">
						Subscribe fields: {(webhookQuery.data?.subscribeFields || []).join(", ")}
					</p>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">{form.id ? "Edit channel" : "Add channel"}</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-4 md:grid-cols-2">
					<div className="space-y-2">
						<Label>Instagram Business Account ID</Label>
						<Input
							value={form.externalAccountId}
							onChange={(e) => setForm((f) => ({ ...f, externalAccountId: e.target.value }))}
							placeholder="IG user / business account id"
						/>
					</div>
					<div className="space-y-2">
						<Label>Facebook Page ID (required for auto-ask DM)</Label>
						<Input
							value={form.pageId}
							onChange={(e) => setForm((f) => ({ ...f, pageId: e.target.value }))}
							placeholder="e.g. 1037523139452922"
						/>
					</div>
					<div className="space-y-2 md:col-span-2">
						<Label>
							Access token {form.id ? "(leave blank to keep existing)" : ""}
						</Label>
						<Input
							type="password"
							value={form.accessToken}
							onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
							placeholder="EAAG…"
						/>
					</div>
					<div className="space-y-2">
						<Label>Lead confidence threshold</Label>
						<Select
							value={String(form.leadThreshold)}
							onValueChange={(v) => setForm((f) => ({ ...f, leadThreshold: Number(v) }))}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="80">80%</SelectItem>
								<SelectItem value="90">90%</SelectItem>
								<SelectItem value="95">95%</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<Label>Status</Label>
						<Select
							value={form.status}
							onValueChange={(v) =>
								setForm((f) => ({ ...f, status: v as "ACTIVE" | "INACTIVE" }))
							}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="ACTIVE">Active</SelectItem>
								<SelectItem value="INACTIVE">Inactive</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox
							checked={form.aiEnabled}
							onCheckedChange={(c) => setForm((f) => ({ ...f, aiEnabled: !!c }))}
							id="aiEnabled"
						/>
						<Label htmlFor="aiEnabled">AI flag (LLM unused in Phase 1)</Label>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox
							checked={form.autoReplyEnabled}
							onCheckedChange={(c) => setForm((f) => ({ ...f, autoReplyEnabled: !!c }))}
							id="autoReply"
						/>
						<Label htmlFor="autoReply">
							Auto-ask for mobile (DM) — sends one message asking for phone, then updates the lead
						</Label>
					</div>
					<div className="space-y-2 md:col-span-2">
						<Label>Default assignee team (round-robin)</Label>
						<div className="max-h-40 space-y-1 overflow-y-auto rounded border p-2">
							{assignees.length === 0 ? (
								<p className="text-xs text-muted-foreground">No sales assignees found.</p>
							) : (
								assignees.map((a) => {
									const id = a.id;
									const checked = form.defaultAssigneeTeamIds.includes(id);
									return (
										<label key={id} className="flex items-center gap-2 text-sm">
											<Checkbox
												checked={checked}
												onCheckedChange={(c) =>
													setForm((f) => ({
														...f,
														defaultAssigneeTeamIds: c
															? [...f.defaultAssigneeTeamIds, id]
															: f.defaultAssigneeTeamIds.filter((x) => x !== id),
													}))
												}
											/>
											{a.name || id}
										</label>
									);
								})
							)}
						</div>
					</div>
					<div className="flex gap-2 md:col-span-2">
						<Button
							type="button"
							disabled={saveMut.isPending || !form.externalAccountId.trim()}
							onClick={() => saveMut.mutate()}
						>
							{form.id ? "Update channel" : "Save channel"}
						</Button>
						{form.id ? (
							<Button type="button" variant="ghost" onClick={() => setForm(emptyForm)}>
								Cancel edit
							</Button>
						) : null}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Connected channels</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					{channelsQuery.isLoading ? (
						<p className="text-sm text-muted-foreground">Loading…</p>
					) : channels.length === 0 ? (
						<p className="text-sm text-muted-foreground">No channels yet.</p>
					) : (
						channels.map((ch) => (
							<div
								key={ch.id}
								className="flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 text-sm"
							>
								<div>
									<div className="font-medium">{ch.externalAccountId}</div>
									<div className="text-xs text-muted-foreground">
										{ch.status} · threshold {ch.leadThreshold}% · token{" "}
										{ch.accessTokenMasked || "—"}
									</div>
								</div>
								<div className="flex gap-2">
									<Button type="button" size="sm" variant="outline" onClick={() => editChannel(ch)}>
										Edit
									</Button>
									<Button
										type="button"
										size="sm"
										variant="secondary"
										disabled={diagnoseMut.isPending}
										onClick={() => diagnoseMut.mutate(ch.id)}
									>
										Diagnose token
									</Button>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										onClick={() => {
											if (confirm("Delete this channel?")) deleteMut.mutate(ch.id);
										}}
									>
										Delete
									</Button>
								</div>
							</div>
						))
					)}
				</CardContent>
			</Card>
		</div>
	);
}
